import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { Button, Icon, Screen, Text, TextInput } from '~/atomic';
import modalStatusService from '~/services/modalStatus';
import { ProcessingView, SuccessView } from './PaymentStatus';
import { useAppDispatch, useAppSelector } from '~/store';
import { fetchEntitlements } from '~/store/entitlements/thunks';
import { selectUserData } from '~/store/user/selectors';
import { formatCentsToBRL } from '~/utils/formatFinance';
import { maskDocument, maskPhone } from '~/utils/masks';
import {
  cancelSubscription,
  changePlan,
  checkout,
  getBillingOffers,
  getInstallmentOptions,
  getMyBilling,
  reactivateSubscription,
  validateCoupon,
  type BillingOffer,
  type BillingPlan,
  type CardInput,
  type CheckoutResult,
  type CouponPreview,
  type InstallmentOption,
  type MyBilling,
} from '~/services/http/requests/billing';
import type { propsIconsNames } from '~/atomic/atoms/Icon/interfaces';

const INTERVAL_UNIT: Record<BillingOffer['interval'], string> = {
  day: 'dia',
  week: 'semana',
  month: 'mês',
  year: 'ano',
};

function cadence(offer: BillingOffer): string {
  const unit = INTERVAL_UNIT[offer.interval];
  return offer.intervalCount === 1 ? `/${unit}` : `a cada ${offer.intervalCount} ${unit}es`;
}

function isAnnual(offer: BillingOffer): boolean {
  return offer.interval === 'year';
}

function perMonthEquivalent(offer: BillingOffer, periodCents: number): number {
  if (offer.interval === 'year') {
    return Math.round(periodCents / (12 * Math.max(1, offer.intervalCount)));
  }
  if (offer.interval === 'month') {
    return Math.round(periodCents / Math.max(1, offer.intervalCount));
  }
  return periodCents;
}

interface OfferCouponEffect {
  finalAmountCents: number;
  installments: number | null;
}

function offerCouponEffect(
  offer: BillingOffer,
  coupon: CouponPreview | null,
): OfferCouponEffect | null {
  if (!coupon) return null;
  const regular = offer.amountCents;
  const discount =
    coupon.couponType === 'percentage'
      ? Math.floor((regular * coupon.value) / 100)
      : coupon.value;
  const final = regular - discount;
  if (final <= 0 || discount <= 0) return null;
  return {
    finalAmountCents: final,
    installments: coupon.discountKind === 'installments' ? coupon.firstNInstallments : null,
  };
}

function isActivePaid(me: MyBilling | null): boolean {
  const s = me?.subscription;
  return (
    !!s &&
    s.planSlug !== 'free' &&
    (s.status === 'active' || s.status === 'past_due' || s.status === 'manual_grant')
  );
}

const onlyDigits = (v: string) => v.replace(/\D/g, '');

function luhnValid(digits: string): boolean {
  if (digits.length < 13) return false;
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let d = digits.charCodeAt(i) - 48;
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

interface SuccessInfo {
  title?: string;
  message?: ReactNode;
  planName?: string;
  cta?: string;
}

interface CardFields {
  number: string;
  holderName: string;
  expiry: string;
  ccv: string;
  doc: string;
  cep: string;
  addressNumber: string;
  email: string;
  phone: string;
}

const EMPTY_CARD: CardFields = {
  number: '',
  holderName: '',
  expiry: '',
  ccv: '',
  doc: '',
  cep: '',
  addressNumber: '',
  email: '',
  phone: '',
};

function cardComplete(c: CardFields): boolean {
  return (
    luhnValid(onlyDigits(c.number)) &&
    c.holderName.trim().length >= 2 &&
    /^\d{2}\/\d{2}$/.test(c.expiry) &&
    onlyDigits(c.ccv).length >= 3 &&
    (onlyDigits(c.doc).length === 11 || onlyDigits(c.doc).length === 14) &&
    onlyDigits(c.cep).length === 8 &&
    c.addressNumber.trim().length >= 1 &&
    /.+@.+\..+/.test(c.email) &&
    onlyDigits(c.phone).length >= 10
  );
}

function showErrorModal(message: string) {
  modalStatusService.show({
    type: 'error',
    icon: 'icon-circle-alert',
    title: 'Não foi possível concluir',
    description: message,
    showFirstButton: true,
    textFirstButton: 'Entendi',
    onClickFirstButton: modalStatusService.hide,
    handleCloseModal: modalStatusService.hide,
  });
}

function buildCard(c: CardFields): CardInput {
  const [mm, yy] = c.expiry.split('/');
  return {
    holderName: c.holderName.trim(),
    number: onlyDigits(c.number),
    expiryMonth: mm,
    expiryYear: `20${yy}`,
    ccv: onlyDigits(c.ccv),
    holder: {
      name: c.holderName.trim(),
      email: c.email.trim(),
      cpfCnpj: onlyDigits(c.doc),
      postalCode: onlyDigits(c.cep),
      addressNumber: c.addressNumber.trim(),
      phone: onlyDigits(c.phone),
    },
  };
}

export default function SubscribeScreen() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUserData);

  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [me, setMe] = useState<MyBilling | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [step, setStep] = useState<'plan' | 'checkout'>('plan');
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [card, setCard] = useState<CardFields>(EMPTY_CARD);
  const [installments, setInstallments] = useState(1);
  const [installmentOptions, setInstallmentOptions] = useState<InstallmentOption[]>([]);
  const [coupon, setCoupon] = useState('');
  const [couponPreview, setCouponPreview] = useState<CouponPreview | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [pending, setPending] = useState<CheckoutResult | null>(null);
  const [successInfo, setSuccessInfo] = useState<SuccessInfo | null>(null);
  const [changeMode, setChangeMode] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !user?.fullName) return;
    prefilled.current = true;
    const rawPhone = onlyDigits(user.contact?.phone ?? '');
    const localPhone =
      rawPhone.length > 11 && rawPhone.startsWith('55') ? rawPhone.slice(2) : rawPhone;
    setCard((c) => ({
      ...c,
      holderName: c.holderName || user.fullName,
      doc: c.doc || maskDocument(user.document ?? ''),
      email: c.email || (user.contact?.email ?? ''),
      phone: c.phone || maskPhone(localPhone),
    }));
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [offersRes, meRes] = await Promise.all([getBillingOffers(), getMyBilling()]);
      const paidPlans = offersRes.data.plans.filter((p) => p.offers.length > 0);
      setPlans(paidPlans);
      setMe(meRes.data);
      const def =
        paidPlans[0]?.offers.find((o) => o.isDefault) ?? paidPlans[0]?.offers[0] ?? null;
      setSelectedOffer((cur) => cur ?? def?.slug ?? null);
    } catch {
      setLoadError('Não foi possível carregar os planos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (step !== 'checkout' || !selectedOffer) return;
    const offer = findOffer(plans, selectedOffer);
    if (!offer || !isAnnual(offer) || offer.maxInstallments <= 1) {
      setInstallmentOptions([]);
      setInstallments(1);
      return;
    }
    let cancelled = false;
    getInstallmentOptions(selectedOffer, couponPreview?.code)
      .then(({ data }) => {
        if (cancelled) return;
        setInstallmentOptions(data.options);
        setInstallments(1);
      })
      .catch(() => {
        if (!cancelled) setInstallmentOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [step, selectedOffer, couponPreview, plans]);

  const checkoutKeyRef = useRef<string>('');

  const pollRef = useRef<number | null>(null);
  useEffect(() => {
    if (!pending) return;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await getMyBilling();
        setMe(data);
        if (isActivePaid(data)) {
          await dispatch(fetchEntitlements());
          setPending(null);
          setProcessing(false);
          setSuccessInfo({
            planName:
              plans.find((p) => p.slug === data.subscription?.planSlug)?.name ??
              'plano premium',
          });
          return;
        }
      } catch {
      }
      if (attempts >= 30 && pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    pollRef.current = window.setInterval(() => void tick(), 4000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [pending, dispatch]);

  const handleSelectOffer = (slug: string) => {
    setSelectedOffer(slug);
  };

  const handleCouponChange = (v: string) => {
    setCoupon(v.toUpperCase());
    if (couponPreview) setCouponPreview(null);
    if (couponError) setCouponError(null);
  };

  const handleValidateCoupon = async () => {
    const code = coupon.trim();
    if (!code || !selectedOffer) return;
    setValidatingCoupon(true);
    setCouponError(null);
    setCouponPreview(null);
    try {
      const { data } = await validateCoupon(selectedOffer, code);
      setCouponPreview(data);
    } catch (e) {
      setCouponError(apiError(e) ?? 'Não foi possível validar o cupom.');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleSubscribe = async () => {
    if (!selectedOffer || !cardComplete(card)) return;
    setSubmitting(true);
    setProcessing(true);
    setActionError(null);
    if (!checkoutKeyRef.current) checkoutKeyRef.current = crypto.randomUUID();
    try {
      const { data } = await checkout(
        selectedOffer,
        buildCard(card),
        couponPreview?.code ?? undefined,
        installments,
        checkoutKeyRef.current,
      );
      checkoutKeyRef.current = '';
      if (data.status === 'active') {
        await dispatch(fetchEntitlements());
        await load();
        setProcessing(false);
        setSuccessInfo({ planName: findPlanName(plans, selectedOffer) });
      } else {
        setPending(data);
      }
    } catch (e) {
      setProcessing(false);
      const msg =
        apiError(e) ?? 'Não foi possível iniciar o pagamento. Confira os dados do cartão.';
      setActionError(msg);
      showErrorModal(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePlan = async (offerSlug: string) => {
    setBusy(true);
    setActionError(null);
    try {
      const { data } = await changePlan(offerSlug, undefined, undefined, crypto.randomUUID());
      await dispatch(fetchEntitlements());
      await load();
      if ((data as { status?: string }).status === 'pending_confirmation') {
        setActionError(
          'Pagamento da troca em análise. Seu novo plano é liberado automaticamente assim que a cobrança for confirmada.',
        );
      } else {
        setChangeMode(false);
        const planName = findPlanName(plans, offerSlug);
        if (data.kind === 'annual_contracted') {
          const inst = data.installments;
          setSuccessInfo({
            title: 'Plano anual contratado!',
            cta: 'Continuar',
            message: (
              <>
                Seu plano <strong>{planName}</strong> já está ativo.{' '}
                {inst && inst.count > 1 ? (
                  <>
                    Cobramos {inst.count}x de{' '}
                    <strong>{formatCentsToBRL(inst.perInstallmentCents)}</strong> (total{' '}
                    {formatCentsToBRL(inst.totalCents)}) no seu cartão.
                  </>
                ) : (
                  <>
                    Cobramos{' '}
                    <strong>
                      {formatCentsToBRL(inst?.totalCents ?? data.to.amountCents)}
                    </strong>{' '}
                    no seu cartão.
                  </>
                )}
              </>
            ),
          });
        } else {
          const isUp = data.kind === 'upgrade';
          setSuccessInfo({
            title: isUp ? 'Plano atualizado!' : 'Plano alterado!',
            cta: 'Continuar',
            message: isUp ? (
              <>
                Você migrou para <strong>{planName}</strong>. Cobramos apenas a diferença
                proporcional de {formatCentsToBRL(data.proratedChargeCents)} no seu cartão.
              </>
            ) : (
              <>
                Você migrou para <strong>{planName}</strong>. A diferença de{' '}
                {formatCentsToBRL(data.creditAddedCents)} virou crédito para as próximas
                cobranças.
              </>
            ),
          });
        }
      }
    } catch (e) {
      const msg = apiError(e) ?? 'Não foi possível trocar de plano agora.';
      setActionError(msg);
      showErrorModal(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    const sub = me?.subscription;
    const committed =
      !!sub?.commitmentEndsAt && new Date(sub.commitmentEndsAt) > new Date();
    const annualPrepaid = committed && sub?.offer?.interval === 'year';
    const keepsBilling = committed && sub?.offer?.interval !== 'year';
    const endsAtIso = committed
      ? sub?.commitmentEndsAt ?? sub?.currentPeriodEndsAt ?? null
      : sub?.currentPeriodEndsAt ?? sub?.commitmentEndsAt ?? null;
    const endsAtLabel = endsAtIso
      ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }).format(
          new Date(endsAtIso),
        )
      : 'o fim do período';
    const message = keepsBilling
      ? `Você está no período de fidelidade. As cobranças mensais CONTINUAM normalmente até ${endsAtLabel}; só nessa data o acesso e as cobranças são encerrados. Deseja agendar o cancelamento mesmo assim?`
      : annualPrepaid
        ? `Você já pagou o período anual. Não haverá novas cobranças; o acesso continua até ${endsAtLabel} e a renovação automática será desativada. Deseja cancelar a renovação?`
        : `Cancelar sua assinatura? Não haverá novas cobranças e o acesso é mantido até ${endsAtLabel}.`;
    if (!window.confirm(message)) return;
    setBusy(true);
    setActionError(null);
    try {
      await cancelSubscription();
      await dispatch(fetchEntitlements());
      await load();
    } catch (e) {
      setActionError(apiError(e) ?? 'Não foi possível cancelar agora.');
    } finally {
      setBusy(false);
    }
  };

  const handleReactivate = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await reactivateSubscription(crypto.randomUUID());
      await dispatch(fetchEntitlements());
      await load();
    } catch (e) {
      setActionError(apiError(e) ?? 'Não foi possível reativar. Tente contratar novamente.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Screen headerTitle="Planos e assinatura" headerShowBack>
      {loading ? (
        <div className="flex h-[60vh] items-center justify-center">
          <Icon name="icon-loader" size={28} color="#3F7D5F" rounded={false} />
        </div>
      ) : loadError ? (
        <div className="flex flex-col items-center gap-medium py-xlarge">
          <Text className="text-bodyMD color-intense-06">{loadError}</Text>
          <Button text="Tentar novamente" onClick={() => void load()} />
        </div>
      ) : successInfo ? (
        <SuccessView {...successInfo} onDone={() => setSuccessInfo(null)} />
      ) : processing ? (
        <ProcessingView
          subtitle={
            pending
              ? 'A confirmação pode levar alguns segundos. Você pode atualizar o status abaixo.'
              : undefined
          }
          onRefresh={
            pending
              ? async () => {
                  try {
                    const { data } = await getMyBilling();
                    setMe(data);
                    if (isActivePaid(data)) {
                      await dispatch(fetchEntitlements());
                      setPending(null);
                      setProcessing(false);
                      setSuccessInfo({
                        planName:
                          plans.find((p) => p.slug === data.subscription?.planSlug)
                            ?.name ?? 'plano premium',
                      });
                    }
                  } catch {
                  }
                }
              : undefined
          }
        />
      ) : changeMode ? (
        <ChangePlanView
          plans={plans}
          subscription={me?.subscription ?? null}
          busy={busy}
          error={actionError}
          onConfirm={handleChangePlan}
          onBack={() => {
            setChangeMode(false);
            setActionError(null);
          }}
        />
      ) : isActivePaid(me) ? (
        <ActiveSubscription
          me={me!}
          busy={busy}
          error={actionError}
          onCancel={handleCancel}
          onReactivate={handleReactivate}
          onChange={() => setChangeMode(true)}
        />
      ) : step === 'checkout' && selectedOffer ? (
        <CheckoutStep
          offer={findOffer(plans, selectedOffer)}
          planName={findPlanName(plans, selectedOffer)}
          couponPreview={couponPreview}
          installmentOptions={installmentOptions}
          installments={installments}
          onInstallments={setInstallments}
          card={card}
          onCard={setCard}
          submitting={submitting}
          onSubscribe={handleSubscribe}
          onBack={() => {
            setStep('plan');
            setActionError(null);
          }}
          error={actionError}
        />
      ) : (
        <PlanChoiceStep
          plans={plans}
          selectedOffer={selectedOffer}
          onSelectOffer={handleSelectOffer}
          coupon={coupon}
          onCoupon={handleCouponChange}
          couponPreview={couponPreview}
          couponError={couponError}
          validatingCoupon={validatingCoupon}
          onValidateCoupon={handleValidateCoupon}
          onContinue={() => {
            setActionError(null);
            setStep('checkout');
          }}
        />
      )}
    </Screen>
  );
}

function findOffer(plans: BillingPlan[], slug: string): BillingOffer | null {
  for (const p of plans) {
    const o = p.offers.find((x) => x.slug === slug);
    if (o) return o;
  }
  return null;
}

function findPlanName(plans: BillingPlan[], slug: string): string {
  const p = plans.find((pl) => pl.offers.some((o) => o.slug === slug));
  return p?.name ?? 'Plano premium';
}

const INVOICE_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  paid: 'Paga',
  overdue: 'Vencida',
  refunded: 'Estornada',
  failed: 'Falhou',
  cancelled: 'Cancelada',
};

function apiError(e: unknown): string | null {
  const msg = (e as { response?: { data?: { message?: string | string[] } } })?.response?.data
    ?.message;
  if (Array.isArray(msg)) return msg[0] ?? null;
  return typeof msg === 'string' ? msg : null;
}

interface PlanChoiceStepProps {
  plans: BillingPlan[];
  selectedOffer: string | null;
  onSelectOffer: (slug: string) => void;
  coupon: string;
  onCoupon: (v: string) => void;
  couponPreview: CouponPreview | null;
  couponError: string | null;
  validatingCoupon: boolean;
  onValidateCoupon: () => void;
  onContinue: () => void;
}

function PlanChoiceStep({
  plans,
  selectedOffer,
  onSelectOffer,
  coupon,
  onCoupon,
  couponPreview,
  couponError,
  validatingCoupon,
  onValidateCoupon,
  onContinue,
}: PlanChoiceStepProps) {
  if (plans.length === 0) {
    return (
      <Text className="py-xlarge text-center text-bodyMD color-intense-06">
        Nenhum plano disponível no momento.
      </Text>
    );
  }

  const amountCoupon =
    couponPreview?.discountKind === 'amount' ? couponPreview : null;
  const installmentCoupon =
    couponPreview?.discountKind === 'installments' ? couponPreview : null;

  return (
    <div className="flex flex-col gap-large pb-xlarge">
      {plans.map((plan) => (
        <section key={plan.slug} className="flex flex-col gap-smaller">
          <div>
            <Text as="h2" className="text-headingH1 color-complement-01">
              {plan.name}
            </Text>
            {plan.description ? (
              <Text className="mt-quark text-bodySM color-intense-06">{plan.description}</Text>
            ) : null}
          </div>

          {plan.benefits && plan.benefits.length > 0 ? (
            <ul className="flex flex-col gap-quark">
              {plan.benefits.map((b) => (
                <li key={b} className="flex items-center gap-nano">
                  <Icon name="icon-check" size={12} color="#1D4733" rounded={false} />
                  <Text className="text-bodySM color-intense-07">{b}</Text>
                </li>
              ))}
            </ul>
          ) : null}

          <OfferList
            offers={plan.offers}
            selectedOffer={selectedOffer}
            onSelectOffer={onSelectOffer}
            couponPreview={couponPreview}
          />
          {plan.autoRenewDefault ? (
            <Text className="text-caption color-intense-05">
              Renovação automática · cancele quando quiser
            </Text>
          ) : null}
        </section>
      ))}

      <section className="flex flex-col gap-smaller">
        <Text className="text-captionLG uppercase tracking-wider color-intense-05">
          Cupom de desconto (opcional)
        </Text>
        <div className="flex items-start gap-smaller">
          <div className="flex-1">
            <TextInput
              placeholder="Digite seu cupom"
              value={coupon}
              onChangeText={(v) => onCoupon(v.toUpperCase())}
              autoCapitalize="characters"
              maxLength={40}
            />
          </div>
          <Button
            text="Validar cupom"
            variant="secondary"
            loading={validatingCoupon}
            disabled={!coupon.trim() || !selectedOffer}
            onClick={onValidateCoupon}
          />
        </div>

        {couponError ? (
          <Text className="rounded-nano bg-warning-01 p-small text-bodySM color-warning-06">
            {couponError}
          </Text>
        ) : null}

        {amountCoupon ? (
          <div className="flex items-center gap-nano rounded-nano bg-support-01 p-small">
            <Icon name="icon-circle-check" size={16} color="#3F7D5F" rounded={false} />
            <Text className="text-bodySM color-primary-01">
              Cupom <strong>{amountCoupon.code}</strong> aplicado em todos os planos ·{' '}
              {amountCoupon.couponType === 'percentage'
                ? `${amountCoupon.value}% de desconto`
                : `${formatCentsToBRL(amountCoupon.value)} de desconto`}
              . Veja o novo valor de cada plano acima.
            </Text>
          </div>
        ) : null}

        {installmentCoupon ? (
          <div className="flex items-center gap-nano rounded-nano bg-support-01 p-small">
            <Icon name="icon-circle-check" size={16} color="#3F7D5F" rounded={false} />
            <Text className="text-bodySM color-primary-01">
              Cupom <strong>{installmentCoupon.code}</strong> aplicado em todos os planos ·{' '}
              {installmentCoupon.couponType === 'percentage'
                ? `${installmentCoupon.value}% de desconto`
                : `${formatCentsToBRL(installmentCoupon.value)} de desconto`}{' '}
              nas primeiras {installmentCoupon.firstNInstallments} parcelas; depois volta ao valor cheio.
            </Text>
          </div>
        ) : null}
      </section>

      <Button
        text="Continuar para o pagamento"
        rightIcon="icon-chevron-right"
        fullWidth
        disabled={!selectedOffer}
        onClick={onContinue}
      />
    </div>
  );
}

interface CheckoutStepProps {
  offer: BillingOffer | null;
  planName: string;
  couponPreview: CouponPreview | null;
  installmentOptions: InstallmentOption[];
  installments: number;
  onInstallments: (n: number) => void;
  card: CardFields;
  onCard: (c: CardFields) => void;
  submitting: boolean;
  onSubscribe: () => void;
  onBack: () => void;
  error: string | null;
}

function CheckoutStep({
  offer,
  planName,
  couponPreview,
  installmentOptions,
  installments,
  onInstallments,
  card,
  onCard,
  submitting,
  onSubscribe,
  onBack,
  error,
}: CheckoutStepProps) {
  if (!offer) {
    return (
      <div className="flex flex-col items-center gap-medium py-xlarge">
        <Text className="text-bodyMD color-intense-06">Oferta indisponível.</Text>
        <Button text="Voltar" variant="secondary" onClick={onBack} />
      </div>
    );
  }

  const ready = cardComplete(card);
  const annual = isAnnual(offer);
  const couponEffect = offerCouponEffect(offer, couponPreview ?? null);
  const couponCode = couponEffect ? couponPreview?.code ?? null : null;
  const couponDiscountCents = couponEffect
    ? offer.amountCents - couponEffect.finalAmountCents
    : 0;
  const couponInstallments = couponEffect?.installments ?? null;
  const basePeriod = couponEffect ? couponEffect.finalAmountCents : offer.amountCents;
  const selectedOption = annual
    ? installmentOptions.find((o) => o.count === installments) ??
      installmentOptions[0] ??
      null
    : null;
  const chargeTotal = selectedOption ? selectedOption.totalCents : basePeriod;

  return (
    <div className="flex flex-col gap-large pb-xlarge">
      <section className="flex flex-col gap-smaller rounded-medium border-2 border-primary-01 bg-support-01 p-medium shadow-sm">
        <div className="flex items-center gap-nano">
          <Icon name="icon-receipt" size={16} color="#1D4733" rounded={false} />
          <Text className="text-captionLG uppercase tracking-wider color-primary-01">
            Resumo do pedido
          </Text>
        </div>
        <div className="flex items-center justify-between gap-small">
          <div>
            <Text className="text-bodyMD font-medium color-complement-01">
              {planName} · {offer.label}
            </Text>
            {offer.commitmentMonths > 0 ? (
              <Text className="text-caption color-intense-05">
                Fidelidade de {offer.commitmentMonths} meses
              </Text>
            ) : null}
          </div>
          <div className="text-right">
            {couponEffect ? (
              <Text className="text-caption color-intense-05 line-through">
                {formatCentsToBRL(offer.amountCents)}
              </Text>
            ) : null}
            {annual && selectedOption && selectedOption.count > 1 ? (
              <>
                <Text className="text-headingH2 color-primary-01">
                  {selectedOption.count}x {formatCentsToBRL(selectedOption.perInstallmentCents)}
                </Text>
                <Text className="text-caption color-intense-05">
                  total {formatCentsToBRL(chargeTotal)}
                  {selectedOption.hasInterest ? ' (com juros)' : ' sem juros'}
                </Text>
              </>
            ) : (
              <>
                <Text className="text-headingH2 color-primary-01">
                  {formatCentsToBRL(chargeTotal)}
                </Text>
                <Text className="text-caption color-intense-05">
                  {annual ? 'à vista' : cadence(offer)}
                </Text>
              </>
            )}
          </div>
        </div>
        {couponEffect && couponDiscountCents > 0 ? (
          <Text className="text-caption color-primary-01">
            Cupom {couponCode} aplicado (-{formatCentsToBRL(couponDiscountCents)}).
          </Text>
        ) : null}
        {!annual && couponInstallments ? (
          <Text className="text-caption color-primary-01">
            Desconto válido nas {couponInstallments} primeiras parcelas.
          </Text>
        ) : null}
      </section>

      {annual && installmentOptions.length > 1 ? (
        <InstallmentSelector
          options={installmentOptions}
          selected={installments}
          onSelect={onInstallments}
        />
      ) : null}

      <CardForm card={card} onCard={onCard} />

      <SecurityBadges />

      {error ? (
        <Text className="rounded-nano bg-warning-01 p-small text-bodySM color-warning-06">
          {error}
        </Text>
      ) : null}

      <Button
        text="Assinar agora"
        rightIcon="icon-chevron-right"
        fullWidth
        loading={submitting}
        disabled={!ready}
        onClick={onSubscribe}
      />
      <Button text="Voltar para os planos" variant="secondary" fullWidth onClick={onBack} />
      <Text className="text-center text-caption color-intense-05">
        Pagamento via cartão de crédito. Você pode cancelar quando quiser; o acesso é liberado
        após a confirmação.
      </Text>
    </div>
  );
}

function SecurityBadges() {
  const badges: Array<{ icon: string; label: string }> = [
    { icon: 'icon-lock', label: 'Pagamento criptografado (SSL)' },
    { icon: 'icon-security', label: 'Ambiente seguro' },
    { icon: 'icon-password-check', label: 'Não armazenamos seu cartão' },
    { icon: 'icon-circle-check', label: 'Cancele quando quiser' },
  ];
  return (
    <section className="grid grid-cols-2 gap-smaller rounded-medium bg-intense-02 p-medium">
      {badges.map((b) => (
        <div key={b.label} className="flex items-center gap-nano">
          <Icon name={b.icon as propsIconsNames} size={16} color="#3F7D5F" rounded={false} />
          <Text className="text-caption color-intense-07">{b.label}</Text>
        </div>
      ))}
    </section>
  );
}

function installmentOptionLabel(o: InstallmentOption): string {
  if (o.count === 1) return `À vista — ${formatCentsToBRL(o.totalCents)}`;
  const suffix = o.hasInterest
    ? `total ${formatCentsToBRL(o.totalCents)} (com juros)`
    : 'sem juros';
  return `${o.count}x de ${formatCentsToBRL(o.perInstallmentCents)} — ${suffix}`;
}

function InstallmentSelector({
  options,
  selected,
  onSelect,
}: {
  options: InstallmentOption[];
  selected: number;
  onSelect: (n: number) => void;
}) {
  const current = options.find((o) => o.count === selected) ?? options[0] ?? null;
  return (
    <section className="flex flex-col gap-smaller">
      <Text className="text-captionLG uppercase tracking-wider color-intense-05">
        Parcelamento no cartão
      </Text>
      <div className="relative">
        <select
          value={selected}
          onChange={(e) => onSelect(Number(e.target.value))}
          className="h-12 w-full appearance-none rounded-medium border border-intense-03 bg-intense-01 px-medium pr-xlarge text-bodyMD color-complement-01 outline-none focus:border-primary-01"
        >
          {options.map((o) => (
            <option key={o.count} value={o.count}>
              {installmentOptionLabel(o)}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-medium top-1/2 -translate-y-1/2">
          <Icon name="icon-chevron-down" size={16} color="#606060" rounded={false} />
        </span>
      </div>
      {current ? (
        <Text className="text-caption color-intense-06">
          {current.count > 1
            ? `${current.count}x de ${formatCentsToBRL(current.perInstallmentCents)} · total ${formatCentsToBRL(current.totalCents)}${current.hasInterest ? ' (com juros)' : ' (sem juros)'}`
            : `Pagamento à vista de ${formatCentsToBRL(current.totalCents)}`}
        </Text>
      ) : null}
    </section>
  );
}

function OfferList({
  offers,
  selectedOffer,
  onSelectOffer,
  couponPreview,
}: {
  offers: BillingOffer[];
  selectedOffer: string | null;
  onSelectOffer: (slug: string) => void;
  couponPreview?: CouponPreview | null;
}) {
  const referenceMonthly = Math.max(
    ...offers.map((o) => perMonthEquivalent(o, o.amountCents)),
  );
  return (
    <div className="flex flex-col gap-smaller">
      {offers.map((offer) => {
        const selected = selectedOffer === offer.slug;
        const effect = offerCouponEffect(offer, couponPreview ?? null);
        const periodPrice = effect ? effect.finalAmountCents : offer.amountCents;
        const monthlyEquiv = perMonthEquivalent(offer, periodPrice);
        const annual = isAnnual(offer);
        const savingsPct =
          referenceMonthly > monthlyEquiv
            ? Math.round((1 - monthlyEquiv / referenceMonthly) * 100)
            : 0;
        const hasSavings = savingsPct > 0;
        return (
          <button
            key={offer.slug}
            type="button"
            onClick={() => onSelectOffer(offer.slug)}
            className={`flex items-stretch justify-between gap-small rounded-medium border p-medium text-left transition-colors ${
              effect
                ? selected
                  ? 'border-primary-01 bg-support-01'
                  : 'border-primary-01/60 bg-support-01/40 hover:border-primary-01'
                : selected
                  ? 'border-primary-01 bg-support-01'
                  : 'border-intense-03 bg-intense-01 hover:border-primary-01/40'
            }`}
          >
            <div className="flex items-center gap-small">
              <span
                className={`flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full border ${
                  selected ? 'border-primary-01 bg-primary-01' : 'border-intense-04'
                }`}
              >
                {selected ? <Icon name="icon-check" size={11} color="#FFFFFF" rounded={false} /> : null}
              </span>
              <div className="flex flex-col gap-quark">
                <div className="flex flex-wrap items-center gap-nano">
                  <Text className="text-bodyMD font-medium color-complement-01">{offer.label}</Text>
                  {hasSavings ? (
                    <span className="rounded-full bg-primary-01 px-nano py-quark text-[10px] font-bold uppercase tracking-wide text-intense-01">
                      -{savingsPct}%
                    </span>
                  ) : null}
                  {offer.commitmentMonths > 0 ? (
                    <span className="rounded-full bg-support-01 px-nano py-quark text-[10px] font-bold uppercase tracking-wide color-primary-01">
                      fidelidade {offer.commitmentMonths}m
                    </span>
                  ) : null}
                </div>
                {annual ? (
                  <Text className="text-caption color-intense-06">
                    Equivalente a {formatCentsToBRL(monthlyEquiv)}/mês
                  </Text>
                ) : null}
                {annual && offer.maxInstallments > 1 ? (
                  <Text className="text-caption color-intense-05">
                    Parcele em até {offer.maxInstallments}x no cartão
                  </Text>
                ) : null}
                {effect?.installments ? (
                  <Text className="text-caption font-medium color-primary-01">
                    Cupom nas {effect.installments} primeiras parcelas
                  </Text>
                ) : null}
              </div>
            </div>
            <div className="flex flex-col items-end justify-center text-right">
              {annual ? (
                <>
                  <Text className="text-headingH2 color-primary-01">
                    {formatCentsToBRL(monthlyEquiv)}
                  </Text>
                  <Text className="text-caption color-intense-06">/mês equivalente</Text>
                  <Text className="text-caption color-intense-05">
                    {formatCentsToBRL(periodPrice)} por ano
                  </Text>
                  {hasSavings ? (
                    <Text className="text-[10px] font-bold uppercase tracking-wide color-primary-01">
                      Economize {savingsPct}%
                    </Text>
                  ) : null}
                </>
              ) : (
                <>
                  <Text className="text-headingH2 color-primary-01">
                    {formatCentsToBRL(periodPrice)}
                  </Text>
                  <Text className="text-caption color-intense-05">{cadence(offer)}</Text>
                  {hasSavings ? (
                    <Text className="text-[10px] font-bold uppercase tracking-wide color-primary-01">
                      Economize {savingsPct}%
                    </Text>
                  ) : null}
                </>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function CardForm({ card, onCard }: { card: CardFields; onCard: (c: CardFields) => void }) {
  const set = (k: keyof CardFields) => (v: string) => onCard({ ...card, [k]: v });
  return (
    <section className="flex flex-col gap-smaller">
      <Text className="text-captionLG uppercase tracking-wider color-intense-05">
        Dados do cartão
      </Text>
      <TextInput
        label="Número do cartão"
        placeholder="0000 0000 0000 0000"
        value={card.number}
        onChangeText={set('number')}
        mask="card"
        keyboardType="numeric"
        inputMode="numeric"
        maxLength={23}
      />
      <TextInput
        label="Nome impresso no cartão"
        placeholder="Como está no cartão"
        value={card.holderName}
        onChangeText={set('holderName')}
        autoCapitalize="characters"
      />
      <div className="grid grid-cols-2 gap-smaller">
        <TextInput
          label="Validade (MM/AA)"
          placeholder="MM/AA"
          value={card.expiry}
          onChangeText={set('expiry')}
          mask="cardExpiry"
          inputMode="numeric"
          maxLength={5}
        />
        <TextInput
          label="CCV"
          placeholder="000"
          value={card.ccv}
          onChangeText={set('ccv')}
          inputMode="numeric"
          maxLength={4}
        />
      </div>

      <Text className="mt-smaller text-captionLG uppercase tracking-wider color-intense-05">
        Dados do titular
      </Text>
      <TextInput
        label="CPF/CNPJ do titular"
        placeholder="000.000.000-00"
        value={card.doc}
        onChangeText={set('doc')}
        mask="document"
        inputMode="numeric"
        maxLength={18}
      />
      <TextInput
        label="E-mail"
        placeholder="email@exemplo.com"
        value={card.email}
        onChangeText={set('email')}
        keyboardType="email-address"
        inputMode="email"
      />
      <div className="grid grid-cols-2 gap-smaller">
        <TextInput
          label="CEP"
          placeholder="00000-000"
          value={card.cep}
          onChangeText={set('cep')}
          mask="cep"
          inputMode="numeric"
          maxLength={9}
        />
        <TextInput
          label="Número"
          placeholder="123"
          value={card.addressNumber}
          onChangeText={set('addressNumber')}
          inputMode="numeric"
          maxLength={10}
        />
      </div>
      <TextInput
        label="Telefone (com DDD)"
        placeholder="(00) 00000-0000"
        value={card.phone}
        onChangeText={set('phone')}
        mask="phone"
        inputMode="tel"
      />
    </section>
  );
}

function ChangePlanView({
  plans,
  subscription,
  busy,
  error,
  onConfirm,
  onBack,
}: {
  plans: BillingPlan[];
  subscription: MyBilling['subscription'];
  busy: boolean;
  error: string | null;
  onConfirm: (slug: string) => void;
  onBack: () => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const currentOfferSlug = subscription?.offer?.slug ?? null;
  const currentMonthly =
    subscription?.amountCents ?? subscription?.offer?.amountCents ?? 0;
  const currentIsAnnual = (subscription?.offer?.interval ?? 'month') === 'year';

  const isBlocked = (_offer: BillingOffer): boolean => currentIsAnnual;

  const availablePlans = plans
    .map((plan) => ({
      plan,
      offers: plan.offers.filter(
        (o) => o.slug !== currentOfferSlug && !isBlocked(o),
      ),
    }))
    .filter((p) => p.offers.length > 0);

  const selectedOffer = availablePlans
    .flatMap((p) => p.offers)
    .find((o) => o.slug === selected);
  const selectedPlanName = selected ? findPlanName(plans, selected) : '';
  const selectedIsMonthlyToAnnual =
    !!selectedOffer && isAnnual(selectedOffer) && !currentIsAnnual;
  const selectedIsUpgrade =
    !!selectedOffer &&
    !selectedIsMonthlyToAnnual &&
    selectedOffer.amountCents > currentMonthly;

  if (confirming && selectedOffer) {
    return (
      <div className="flex flex-col gap-large pb-xlarge">
        <div className="flex flex-col items-center gap-smaller rounded-medium border border-support-01 bg-intense-02 p-large text-center">
          <span className="flex h-[48px] w-[48px] items-center justify-center rounded-full bg-support-01">
            <Icon
              name={
                selectedIsMonthlyToAnnual
                  ? 'icon-calendar'
                  : selectedIsUpgrade
                    ? 'icon-arrow-up'
                    : 'icon-supply-money'
              }
              size={22}
              color="#3F7D5F"
              rounded={false}
            />
          </span>
          <Text as="h2" className="text-headingH1 color-complement-01">
            Confirmar {selectedIsMonthlyToAnnual ? 'plano anual' : selectedIsUpgrade ? 'upgrade' : 'troca'}?
          </Text>
          <Text className="text-bodyMD color-intense-06">
            Você está migrando para <strong>{selectedPlanName}</strong> ·{' '}
            {selectedOffer.label} ({formatCentsToBRL(selectedOffer.amountCents)}
            {cadence(selectedOffer)}).
          </Text>
          <Text className="text-bodySM color-intense-06">
            {selectedIsMonthlyToAnnual
              ? 'O plano anual é contratado na hora: cobramos o valor anual no seu cartão e o acesso passa a valer imediatamente.'
              : selectedIsUpgrade
                ? 'Cobramos apenas a diferença proporcional do período no seu cartão e o novo plano é liberado na hora.'
                : 'A diferença proporcional vira crédito para as próximas cobranças e o novo plano passa a valer imediatamente.'}
            {selectedOffer.commitmentMonths > 0
              ? ` Esta oferta tem fidelidade de ${selectedOffer.commitmentMonths} meses.`
              : ''}
          </Text>
        </div>
        {error ? (
          <Text className="rounded-nano bg-warning-01 p-small text-bodySM color-warning-06">
            {error}
          </Text>
        ) : null}
        <Button
          text={
            selectedIsMonthlyToAnnual
              ? 'Sim, contratar plano anual'
              : selectedIsUpgrade
                ? 'Sim, fazer upgrade'
                : 'Sim, trocar de plano'
          }
          fullWidth
          loading={busy}
          onClick={() => onConfirm(selected!)}
        />
        <Button
          text="Voltar"
          variant="secondary"
          fullWidth
          disabled={busy}
          onClick={() => setConfirming(false)}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-large pb-xlarge">
      <Text className="text-bodySM color-intense-06">
        Escolha o novo plano. Em upgrades cobramos apenas a diferença proporcional no seu cartão;
        em downgrades a diferença vira crédito para as próximas cobranças. Ao migrar para um plano
        anual, ele é contratado na hora e o valor anual é cobrado no seu cartão.
      </Text>
      {currentIsAnnual ? (
        <Text className="rounded-nano bg-support-01 p-small text-bodySM color-primary-01">
          Você está em um plano anual. Para mudar de plano, aguarde o fim do período contratado —
          você pode desativar a renovação automática e contratar outro plano ao final.
        </Text>
      ) : null}
      {availablePlans.length === 0 ? (
        <Text className="py-large text-center text-bodyMD color-intense-06">
          Nenhuma troca disponível no momento.
        </Text>
      ) : null}
      {availablePlans.map(({ plan, offers }) => (
        <section key={plan.slug} className="flex flex-col gap-smaller">
          <Text as="h2" className="text-headingH1 color-complement-01">
            {plan.name}
          </Text>
          <OfferList
            offers={offers}
            selectedOffer={selected}
            onSelectOffer={setSelected}
          />
        </section>
      ))}
      {error ? (
        <Text className="rounded-nano bg-warning-01 p-small text-bodySM color-warning-06">
          {error}
        </Text>
      ) : null}
      <Button
        text="Revisar troca"
        rightIcon="icon-chevron-right"
        fullWidth
        disabled={!selected}
        onClick={() => selected && setConfirming(true)}
      />
      <button
        type="button"
        onClick={onBack}
        className="w-full rounded-small py-smaller text-bodyMD color-intense-05 transition-colors hover:bg-intense-02"
      >
        Voltar
      </button>
    </div>
  );
}

function ActiveSubscription({
  me,
  busy,
  error,
  onCancel,
  onReactivate,
  onChange,
}: {
  me: MyBilling;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onReactivate: () => void;
  onChange: () => void;
}) {
  const sub = me.subscription!;
  const fmt = (iso: string | null) =>
    iso
      ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }).format(
          new Date(iso),
        )
      : null;
  const renews = fmt(sub.renewsAt);
  const scheduledCancel = sub.cancelAtPeriodEnd;
  const committed =
    !!sub.commitmentEndsAt && new Date(sub.commitmentEndsAt) > new Date();
  const annualPrepaid = committed && sub.offer?.interval === 'year';
  const keepsBilling = scheduledCancel && committed && !annualPrepaid;
  const endsAt = keepsBilling
    ? fmt(sub.commitmentEndsAt)
    : fmt(sub.currentPeriodEndsAt ?? sub.commitmentEndsAt);

  const renewalPreview = sub.renewalPreview;
  const renewalDays = renewalPreview
    ? Math.ceil((new Date(renewalPreview.chargeAt).getTime() - Date.now()) / 86_400_000)
    : null;
  const showRenewalNotice =
    !!renewalPreview && !scheduledCancel && renewalDays != null && renewalDays >= 0 && renewalDays <= 30;

  return (
    <div className="flex flex-col gap-medium pb-xlarge">
      <div className="flex flex-col gap-smaller rounded-medium border border-support-01 bg-intense-01 p-large shadow-level1">
        <div className="flex items-center justify-between">
          <Text className="text-captionLG uppercase tracking-wider color-intense-05">
            {scheduledCancel ? 'Cancelamento agendado' : 'Assinatura ativa'}
          </Text>
          <span
            className={`rounded-full px-nano py-quark text-[10px] font-bold uppercase tracking-wide ${
              scheduledCancel
                ? 'bg-warning-01 color-warning-06'
                : sub.status === 'past_due'
                  ? 'bg-alert-01 text-alert-06'
                  : 'bg-support-01 color-primary-01'
            }`}
          >
            {scheduledCancel
              ? keepsBilling
                ? 'Em fidelidade'
                : annualPrepaid
                  ? 'Renovação cancelada'
                  : 'Encerra em breve'
              : sub.status === 'past_due'
                ? 'Pagamento pendente'
                : 'Ativa'}
          </span>
        </div>
        {sub.offer ? (
          <div className="flex items-baseline gap-quark">
            <Text className="text-display1 color-primary-01">
              {formatCentsToBRL(sub.amountCents ?? sub.offer.amountCents)}
            </Text>
            <Text className="text-bodySM color-intense-05">{cadence(sub.offer)}</Text>
          </div>
        ) : null}
        {sub.offer ? (
          <Text className="text-bodySM color-intense-06">Oferta: {sub.offer.label}</Text>
        ) : null}
        {scheduledCancel && endsAt ? (
          keepsBilling ? (
            <>
              <Text className="text-caption color-warning-06">
                Cobranças mensais continuam até {endsAt} (fim da fidelidade).
              </Text>
              <Text className="text-caption color-intense-06">
                O acesso e as cobranças são encerrados nessa data.
              </Text>
            </>
          ) : annualPrepaid ? (
            <>
              <Text className="text-caption color-warning-06">
                Sem novas cobranças · acesso garantido até {endsAt}.
              </Text>
              <Text className="text-caption color-intense-06">
                Você já pagou o período anual; a renovação automática foi desativada.
              </Text>
            </>
          ) : (
            <Text className="text-caption color-warning-06">
              Sem novas cobranças · acesso garantido até {endsAt}.
            </Text>
          )
        ) : renews ? (
          <div className="flex items-center gap-quark text-caption color-intense-06">
            <Icon name="icon-calendar" size={12} color="#606060" rounded={false} />
            <span>Renova em {renews}</span>
          </div>
        ) : null}
        {!scheduledCancel ? (
          <Text className="text-caption color-intense-05">
            Renovação automática {sub.autoRenew ? 'ativada' : 'desativada'}
          </Text>
        ) : null}
        {sub.creditBalanceCents > 0 ? (
          <Text className="text-caption color-primary-01">
            Crédito disponível: {formatCentsToBRL(sub.creditBalanceCents)} (abate as próximas cobranças)
          </Text>
        ) : null}
      </div>

      {showRenewalNotice && renewalPreview ? (
        <div className="flex flex-col gap-quark rounded-medium border border-support-01 bg-intense-02 p-medium">
          <div className="flex items-center gap-nano">
            <Icon name="icon-calendar" size={16} color="#1D4733" rounded={false} />
            <Text className="text-captionLG uppercase tracking-wider color-primary-01">
              Renovação em {renewalDays} {renewalDays === 1 ? 'dia' : 'dias'}
            </Text>
          </div>
          <Text className="text-bodySM color-intense-07">
            Sua renovação anual de{' '}
            <strong>{formatCentsToBRL(renewalPreview.totalCents)}</strong>
            {renewalPreview.installmentCount > 1
              ? ` (${renewalPreview.installmentCount}x de ${formatCentsToBRL(renewalPreview.perInstallmentCents)})`
              : ''}{' '}
            será cobrada em {fmt(renewalPreview.chargeAt)}.
          </Text>
          <Text className="text-caption color-intense-06">
            Para não ser cobrado, cancele o plano — você continua usando até o fim do período atual.
          </Text>
        </div>
      ) : null}

      {me.invoices.length > 0 ? (
        <div className="flex flex-col gap-smaller rounded-medium border border-intense-03 p-medium">
          <Text className="text-captionLG uppercase tracking-wider color-intense-05">
            Últimas cobranças
          </Text>
          {me.invoices.slice(0, 5).map((inv) => {
            const when = fmt(inv.paidAt ?? inv.dueDate);
            return (
              <div key={inv.id} className="flex items-center justify-between">
                <div>
                  <Text className="text-bodyMD color-complement-01">
                    {formatCentsToBRL(inv.amountCents)}
                  </Text>
                  <Text className="text-caption color-intense-05">
                    {INVOICE_STATUS_LABEL[inv.status] ?? inv.status}
                    {when ? ` · ${when}` : ''}
                  </Text>
                </div>
                {inv.invoiceUrl ? (
                  <button
                    type="button"
                    onClick={() => window.open(inv.invoiceUrl!, '_blank')}
                    className="text-bodySM color-primary-01"
                  >
                    Ver recibo
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {error ? (
        <Text className="rounded-nano bg-warning-01 p-small text-bodySM color-warning-06">
          {error}
        </Text>
      ) : null}

      {scheduledCancel ? (
        <Button text="Reativar assinatura" fullWidth loading={busy} onClick={onReactivate} />
      ) : (
        <>
          <Button text="Trocar de plano" variant="secondary" fullWidth onClick={onChange} />
          <Button
            text="Cancelar assinatura"
            variant="outline"
            fullWidth
            loading={busy}
            onClick={onCancel}
          />
        </>
      )}
    </div>
  );
}
